import { useForm } from "react-hook-form";
import { FormEvent, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Switch } from "@babackapp/ui/components/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@babackapp/ui/components/form";
import {
  overrideRuleFormSchema,
  createDefaultConditions,
  createDefaultAction,
  convertRuleToFormValues,
  type OverrideRuleFormValuesInput,
  FieldIdDefinition,
} from "@/lib/override-rules";
import { RuleConditionsBuilder } from "./rule-conditions-builder";
import { ActionSelector } from "./action-selector";
import { ScopeSelector, type CarrierOption } from "./scope-selector";
import type {
  ShopOverrideRuleDto,
  ShopOverrideRuleActionDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import type { ScopeFormValues } from "@/lib/override-rules";

interface OverrideRuleFormProps {
  existingRule?: ShopOverrideRuleDto;
  actionType: ShopOverrideRuleActionDto["type"];
  actionValueOptions?: { label: string; key: string }[];
  onSubmit: (data: OverrideRuleFormValuesInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
  excludeFields?: FieldIdDefinition[];
  showScope?: boolean;
  carriers?: CarrierOption[];
  defaultScope?: ScopeFormValues;
}

export function OverrideRuleForm({
  existingRule,
  actionType,
  actionValueOptions,
  onSubmit,
  onCancel,
  isLoading,
  excludeFields,
  showScope = false,
  carriers = [],
  defaultScope,
}: OverrideRuleFormProps) {
  const t = useTranslation();
  const isEditing = !!existingRule;

  // scope is not yet in the generated type — read it via unknown cast
  const existingScope = existingRule
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((existingRule as any).scope ?? null)
    : null;

  const defaultValues = existingRule
    ? { ...convertRuleToFormValues(existingRule), scope: existingScope }
    : {
        name: "",
        description: "",
        isActive: true,
        priority: 1,
        conditions: createDefaultConditions(),
        action: createDefaultAction(actionType),
        scope: defaultScope ?? null,
      };

  const form = useForm<OverrideRuleFormValuesInput>({
    resolver: zodResolver(overrideRuleFormSchema),
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (existingRule) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const scope = (existingRule as any).scope ?? null;
      form.reset({ ...convertRuleToFormValues(existingRule), scope });
    }
  }, [existingRule, form]);

  const handleSubmit = (data: OverrideRuleFormValuesInput) => {
    onSubmit(data);
  };

  const handleSubmitClick = async (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const isFormValid = await form.trigger();
    if (!isFormValid) {
      return false;
    }
    await form.handleSubmit(handleSubmit)();
  };

  return (
    <div className="space-y-6 p-1 overflow-hidden">
      <Form {...form}>
        <form onSubmit={(e) => handleSubmitClick(e)} className="space-y-4">
          <div className="flex flex-wrap-reverse gap-4 items-start justify-between">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("overrideRules.misc.ruleName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("overrideRules.misc.ruleName")}
                      {...field}
                      className="h-8 min-w-[8rem]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 mb-1">
                  <FormControl>
                    <Switch
                      className="mb-0"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-sm">
                    {t("overrideRules.misc.active")}
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("overrideRules.misc.ruleDescription")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("overrideRules.misc.ruleDescription")}
                    {...field}
                    className="h-8"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showScope && (
            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("overrideRules.scope.label")}</FormLabel>
                  <FormControl>
                    <ScopeSelector
                      value={field.value ?? null}
                      onChange={field.onChange}
                      carriers={carriers}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="conditions"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RuleConditionsBuilder
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                    excludeFields={excludeFields}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="action.parameters"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ActionSelector
                    actionType={actionType}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                    readonly={!actionType}
                    actionValueOptions={actionValueOptions}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              size="sm"
            >
              {t("misc.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || form.formState.isSubmitting}
              size="sm"
            >
              {isLoading
                ? t("misc.saving")
                : isEditing
                  ? t("misc.update")
                  : t("misc.create")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
